import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MdPeople, MdClose, MdFiberManualRecord } from 'react-icons/md';
import '../styles/SMS.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const emptyNewUserForm = { username: '', first_name: '', last_name: '', email: '', phone_number: '', password: '', roles: ['user', 'receives_reminders'] };

function SMS({ user, onUnreadChange }) {
  const [members, setMembers] = useState([]);
  const [unknownThreads, setUnknownThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null); // { type: 'known', member } | { type: 'unknown', phone_number }
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [saveAsUserForm, setSaveAsUserForm] = useState(null);
  const [saveAsUserError, setSaveAsUserError] = useState('');
  const [hideAttendanceTagged, setHideAttendanceTagged] = useState(true);
  const [placeholderNameInput, setPlaceholderNameInput] = useState('');
  const [membersOpen, setMembersOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState(null); // { x, y, message }
  const chatEndRef = useRef(null);
  const longPressTimer = useRef(null);

  const roles = user?.roles || [];
  const canSend = roles.includes('message_sender') || roles.includes('admin');
  const canManageUsers = roles.includes('user_manager') || roles.includes('admin');
  const canReclassify = roles.includes('message_sender') || roles.includes('broadcast_sender') || roles.includes('admin');

  useEffect(() => {
    fetchThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedThread) return;
    fetchConversation(selectedThread);
    markThreadRead(selectedThread);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedThread]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!contextMenu) return;
    const closeMenu = () => setContextMenu(null);
    window.addEventListener('click', closeMenu);
    window.addEventListener('scroll', closeMenu, true);
    window.addEventListener('keydown', closeMenu);
    return () => {
      window.removeEventListener('click', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('keydown', closeMenu);
    };
  }, [contextMenu]);

  useEffect(() => {
    if (selectedThread?.type === 'unknown') {
      const t = unknownThreads.find((th) => th.phone_number === selectedThread.phone_number);
      setPlaceholderNameInput(t?.display_name || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedThread, unknownThreads]);

  const fetchThreads = async () => {
    try {
      const summaryRes = await axios.get(`${API_BASE_URL}/messages/thread_summaries/`);
      const known = summaryRes.data.known || [];
      const unknown = summaryRes.data.unknown || [];
      setMembers(known);
      setUnknownThreads(unknown);
      onUnreadChange?.(summaryRes.data.total_unread || 0);
      setSelectedThread((prev) => {
        if (prev) return prev;
        if (known.length > 0) return { type: 'known', member: known[0] };
        if (unknown.length > 0) return { type: 'unknown', phone_number: unknown[0].phone_number };
        return null;
      });
    } catch (err) {
      console.error('Error fetching threads:', err);
    }
  };

  const fetchConversation = async (thread) => {
    try {
      const response = thread.type === 'known'
        ? await axios.get(`${API_BASE_URL}/messages/conversation/?user_id=${thread.member.id}`)
        : await axios.get(`${API_BASE_URL}/messages/conversation_by_phone/?phone_number=${encodeURIComponent(thread.phone_number)}`);
      setMessages(response.data);
    } catch (err) {
      console.error('Error fetching conversation:', err);
    }
  };

  const markThreadRead = async (thread) => {
    try {
      const payload = thread.type === 'known'
        ? { user_id: thread.member.id }
        : { phone_number: thread.phone_number };
      await axios.post(`${API_BASE_URL}/messages/mark_thread_read/`, payload);
      await fetchThreads();
    } catch (err) {
      console.error('Error marking thread read:', err);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedThread || (selectedThread.type !== 'known' && selectedThread.type !== 'unknown')) return;
    setSending(true);
    setError('');
    try {
      const payload = selectedThread.type === 'known'
        ? { user_id: selectedThread.member.id, text: replyText.trim() }
        : { phone_number: selectedThread.phone_number, text: replyText.trim() };
      await axios.post(`${API_BASE_URL}/messages/send/`, payload);
      setReplyText('');
      await fetchConversation(selectedThread);
      await fetchThreads();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const openMessageContextMenu = (e, msg) => {
    if (!canReclassify || msg.direction !== 'inbound' || selectedThread?.type !== 'known') return;
    e.preventDefault();
    const x = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const y = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    setContextMenu({ x, y, message: msg });
  };

  const handleLongPressStart = (e, msg) => {
    if (!canReclassify || msg.direction !== 'inbound' || selectedThread?.type !== 'known') return;
    const touch = e.touches?.[0];
    const x = touch?.clientX ?? 0;
    const y = touch?.clientY ?? 0;
    longPressTimer.current = setTimeout(() => {
      setContextMenu({ x, y, message: msg });
    }, 500);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleReclassify = async (messageId, newStatus) => {
    setContextMenu(null);
    try {
      await axios.post(`${API_BASE_URL}/messages/${messageId}/reclassify/`, { status: newStatus });
      await fetchConversation(selectedThread);
      await fetchThreads();
    } catch (err) {
      console.error('Error reclassifying message:', err);
      setError(err.response?.data?.error || 'Failed to update attendance reply');
    }
  };

  const savePlaceholderName = async () => {
    if (!selectedThread || selectedThread.type !== 'unknown') return;
    const trimmed = placeholderNameInput.trim();
    const phoneNumber = selectedThread.phone_number;
    try {
      await axios.post(`${API_BASE_URL}/messages/set_unknown_name/`, {
        phone_number: phoneNumber,
        display_name: trimmed,
      });
      setUnknownThreads((prev) =>
        prev.map((t) => (t.phone_number === phoneNumber ? { ...t, display_name: trimmed } : t))
      );
    } catch (err) {
      console.error('Error saving placeholder name:', err);
    }
  };

  const openSaveAsUser = (phoneNumber) => {
    setSaveAsUserForm({ ...emptyNewUserForm, phone_number: phoneNumber });
    setSaveAsUserError('');
  };

  const handleSaveAsUser = async (e) => {
    e.preventDefault();
    setSaveAsUserError('');
    try {
      const res = await axios.post(`${API_BASE_URL}/users/`, saveAsUserForm);
      setSaveAsUserForm(null);
      await fetchThreads();
      setSelectedThread({ type: 'known', member: res.data });
    } catch (err) {
      setSaveAsUserError(err.response?.data?.error || 'Failed to save user');
    }
  };

  const memberName = (m) =>
    `${m.user.first_name} ${m.user.last_name}`.trim() || m.user.username;

  const isThreadActive = (thread) => {
    if (!selectedThread) return false;
    if (thread.type !== selectedThread.type) return false;
    return thread.type === 'known'
      ? thread.member.id === selectedThread.member?.id
      : thread.phone_number === selectedThread.phone_number;
  };

  const headerName = selectedThread?.type === 'known' ? memberName(selectedThread.member) : null;
  const headerPhone = selectedThread?.type === 'known' ? selectedThread.member.phone_number : selectedThread?.phone_number;

  const visibleMessages = hideAttendanceTagged
    ? messages.filter((msg) => !msg.parsed_as)
    : messages;

  return (
    <div className="sms-container">
      <div className={`sms-sidebar ${membersOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h3 className="sidebar-title">Members</h3>
          <button
            type="button"
            className="sidebar-close-btn"
            onClick={() => setMembersOpen(false)}
            title="Close members list"
          >
            <MdClose size={20} />
          </button>
        </div>
        <ul className="member-list">
          {members.map((m) => (
            <li
              key={`known-${m.id}`}
              className={`member-item ${isThreadActive({ type: 'known', member: m }) ? 'active' : ''}`}
              onClick={() => { setSelectedThread({ type: 'known', member: m }); setMembersOpen(false); }}
            >
              <span className="member-name">{memberName(m)}</span>
              {m.has_unread && (
                <span className="unread-indicator" title="Unread messages">
                  <MdFiberManualRecord size={10} />
                </span>
              )}
            </li>
          ))}
          {unknownThreads.length > 0 && (
            <>
              <li className="member-list-divider">Unknown numbers</li>
              {unknownThreads.map((t) => (
                <li
                  key={`unknown-${t.phone_number}`}
                  className={`member-item unknown-item ${isThreadActive({ type: 'unknown', phone_number: t.phone_number }) ? 'active' : ''}`}
                  onClick={() => { setSelectedThread({ type: 'unknown', phone_number: t.phone_number }); setMembersOpen(false); }}
                >
                  <span className="member-name"><span className="unknown-badge">Unknown</span> {t.display_name || t.phone_number}</span>
                  {t.has_unread && (
                    <span className="unread-indicator" title="Unread messages">
                      <MdFiberManualRecord size={10} />
                    </span>
                  )}
                </li>
              ))}
            </>
          )}
        </ul>
      </div>

      <div className="sms-chat">
        {selectedThread ? (
          <>
            <div className="chat-header">
              <button
                type="button"
                className="members-toggle-btn"
                onClick={() => setMembersOpen(true)}
                title="Show members"
              >
                <MdPeople size={22} />
              </button>
              {selectedThread.type === 'known' ? (
                <span className="chat-header-name">{headerName}</span>
              ) : (
                <input
                  type="text"
                  className="placeholder-name-input"
                  placeholder="Unknown number"
                  value={placeholderNameInput}
                  onChange={(e) => setPlaceholderNameInput(e.target.value)}
                  onBlur={savePlaceholderName}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.target.blur();
                    }
                  }}
                />
              )}
              <span className="chat-header-phone">{headerPhone}</span>
              {selectedThread.type === 'known' && (
                <label className="hide-attendance-toggle">
                  <input
                    type="checkbox"
                    checked={hideAttendanceTagged}
                    onChange={(e) => setHideAttendanceTagged(e.target.checked)}
                  />
                  Hide attendance replies
                </label>
              )}
              {selectedThread.type === 'unknown' && canManageUsers && (
                <button
                  type="button"
                  className="btn-save-as-user"
                  onClick={() => openSaveAsUser(selectedThread.phone_number)}
                >
                  Save as New User
                </button>
              )}
            </div>

            <div className="chat-messages">
              {visibleMessages.length === 0 ? (
                <p className="no-messages">No messages yet.</p>
              ) : (
                visibleMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`bubble-wrapper ${msg.direction === 'inbound' ? 'inbound' : 'outbound'}`}
                  >
                    <div
                      className={`bubble ${msg.direction}`}
                      onContextMenu={(e) => openMessageContextMenu(e, msg)}
                      onTouchStart={(e) => handleLongPressStart(e, msg)}
                      onTouchEnd={handleLongPressEnd}
                      onTouchMove={handleLongPressEnd}
                    >
                      {msg.direction === 'outbound' && msg.sent_by_name && (
                        <span className="bubble-sent-by">{msg.sent_by_name}</span>
                      )}
                      <p className="bubble-text">{msg.content}</p>
                      {msg.parsed_as && msg.direction === 'inbound' && (
                        <span className={`parsed-tag ${msg.parsed_as}`}>
                          {msg.parsed_as.replace('_', ' ')}
                        </span>
                      )}
                      <span className="bubble-time">
                        {new Date(msg.created_at).toLocaleString('da-DK', {
                          day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                ))
              )}
              <div ref={chatEndRef} />
            </div>

            {canSend && (selectedThread.type === 'known' || selectedThread.type === 'unknown') && (
              <form className="chat-input-bar" onSubmit={handleSend}>
                {error && <div className="chat-error">{error}</div>}
                <textarea
                  className="chat-input"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type a message..."
                  maxLength={160}
                  rows={2}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend(e);
                    }
                  }}
                />
                <div className="chat-input-footer">
                  <span className="char-count">{replyText.length}/160</span>
                  <button type="submit" className="btn-send" disabled={sending || !replyText.trim()}>
                    {sending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </form>
            )}

            {selectedThread.type === 'unknown' && (
              <div className="unknown-notice">
                This number isn't a known member.
              </div>
            )}
          </>
        ) : (
          <div className="no-selection">Select a conversation to view.</div>
        )}
      </div>

      {contextMenu && (
        <ul
          className="sms-context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <li onClick={() => handleReclassify(contextMenu.message.id, 'attending')}>Attending</li>
          <li onClick={() => handleReclassify(contextMenu.message.id, 'not_attending')}>Not attending</li>
          <li className="sms-context-menu-cancel" onClick={() => setContextMenu(null)}>No change</li>
        </ul>
      )}

      {saveAsUserForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Save as New User</h3>
            {saveAsUserError && <div className="error-msg">{saveAsUserError}</div>}
            <form onSubmit={handleSaveAsUser} className="add-user-form">
              <div className="form-row">
                <input placeholder="First name" value={saveAsUserForm.first_name} onChange={(e) => setSaveAsUserForm({ ...saveAsUserForm, first_name: e.target.value })} required />
                <input placeholder="Last name" value={saveAsUserForm.last_name} onChange={(e) => setSaveAsUserForm({ ...saveAsUserForm, last_name: e.target.value })} required />
              </div>
              <input placeholder="Username" value={saveAsUserForm.username} onChange={(e) => setSaveAsUserForm({ ...saveAsUserForm, username: e.target.value })} required />
              <input type="email" placeholder="Email" value={saveAsUserForm.email} onChange={(e) => setSaveAsUserForm({ ...saveAsUserForm, email: e.target.value })} />
              <input placeholder="Phone number" value={saveAsUserForm.phone_number} onChange={(e) => setSaveAsUserForm({ ...saveAsUserForm, phone_number: e.target.value })} required />
              <input type="password" placeholder="Temporary password" value={saveAsUserForm.password} onChange={(e) => setSaveAsUserForm({ ...saveAsUserForm, password: e.target.value })} required />
              <div className="form-actions">
                <button type="submit" className="btn-save">Save</button>
                <button type="button" className="btn-cancel" onClick={() => setSaveAsUserForm(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SMS;
